import React, { FC, useEffect, useRef, useState } from 'react';

import { Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material';
import { Link } from 'react-router-dom';

import VideoClipIcon from '~/assets/icons/VideoClipIcon';
import CardLabels from '~/components/Card/components/CardLabels/CardLabels';
import CategoryLabel from '~/components/UI/CategoryLabel/CategoryLabel';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { buildGlossaryRoute } from '~/routes';
import { SMALL_MARGIN, SMALL_MARGIN_PX } from '~/theme';
import getAttributeByLocale from '~/utils/getAttributeByLocale';

interface GlossaryGridHitProps {
  hit?: any;
  onClick: () => void;
}

const GlossaryGridHit: FC<GlossaryGridHitProps> = ({ hit, onClick }) => {
  const theme = useTheme();
  const { t, i18n } = useTenantTranslation();
  const [descriptionLines, setDescriptionLines] = useState(2);

  const blockRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (blockRef.current?.clientHeight) {
      const lines = Math.floor((blockRef.current.clientHeight - 42) / 23);
      setDescriptionLines(lines);
    }
  }, [hit]);

  return (
    <Wrapper>
      <CardLabels>
        <CategoryLabel
          key={hit?.videoUrl}
          className={'cardLabel'}
          name={t('page.discover.glossary')}
          textColor={theme.palette.common.blueDark}
          bgColor={theme.palette.common.yellow}
          Icon={Boolean(hit?.video_url) ? StyledVideoClipIcon : undefined}
        />
        {hit?.categories && hit.categories?.[0] && (
          <StyledCategoryLabel
            className={'cardLabel'}
            key={hit.categories?.[0]?.id}
            name={getAttributeByLocale(hit.categories?.[0]?.title, i18n.language)}
            withDots
          />
        )}
      </CardLabels>
      <Link to={buildGlossaryRoute(hit?.slug)} onClick={onClick}>
        <Title variant="h3">{getAttributeByLocale(hit?.title, i18n.language)}</Title>
      </Link>
      <InfoWrapper ref={blockRef}>
        <Description lines={descriptionLines}>
          {getAttributeByLocale(hit?.description, i18n.language)}
        </Description>
        <StyledLink to={buildGlossaryRoute(hit?.slug)} onClick={onClick}>
          {t('page.discover.readMore')}
        </StyledLink>
      </InfoWrapper>
    </Wrapper>
  );
};

export default GlossaryGridHit;

const StyledCategoryLabel = styled(CategoryLabel)`
  & > p {
    max-width: 104px;
  }
`;
const StyledVideoClipIcon = styled(VideoClipIcon)`
  color: ${({ theme }) => theme.palette.common.blueDark};
`;

const Wrapper = styled('div')`
  padding: 24px;
  background: ${({ theme }) => theme.palette.common.card.background.light};
  border-radius: 10px;
  height: 338px;
  display: flex;
  flex-direction: column;
  margin-bottom: ${SMALL_MARGIN_PX};

  & .cardLabel {
    margin-bottom: ${SMALL_MARGIN / 2}px;

    ${({ theme }) => theme.breakpoints.up('md')} {
      margin-bottom: ${SMALL_MARGIN_PX};
    }
  }
`;
const Title = styled(Typography)`
  && {
    color: ${({ theme }) => theme.palette.common.white};
    font-weight: bold;
    font-size: 24px;
    line-height: 28px;
    margin-bottom: ${SMALL_MARGIN / 2}px;
  }
`;
const InfoWrapper = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
`;
const Description = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'lines',
})<{ lines: number }>`
  color: ${({ theme }) => theme.palette.common.text.secondaryToPrimary};
  font-size: 18px;
  line-height: 24px;
  display: -webkit-box;
  overflow: hidden;
  white-space: inherit;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${({ lines }) => lines};
`;
const StyledLink = styled(Link)`
  margin-top: ${SMALL_MARGIN / 2}px;
  color: ${({ theme }) => theme.palette.common.violetSecondary};
  font-size: 18px;
  line-height: 24px;

  &:hover {
    text-decoration: underline;
  }
`;
